import api from "@/lib/axios";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Permission {
  id: number;
  codename: string;
  resource: string;
  action: string;
  description: string;
  /** May this permission be granted to a franchise entity? */
  is_delegatable: boolean;
  /**
   * True when `is_delegatable` can never be turned on, whatever its current
   * value -- the backend refuses it. Render the toggle disabled rather than
   * offering a control that always 400s.
   */
  always_protected: boolean;
}

export interface AdminRole {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
}

export interface AdminRoleDetail extends AdminRole {
  permissions: Permission[];
  created_at: string;
}

export interface UserRoleAssignment {
  id: number;
  user: number;
  username: string;
  role: number;
  role_name: string;
  role_display_name: string;
  scope_type: string;
  scope_reference_id: number | null;
  scope_label: string;
  is_active: boolean;
  assigned_by: number | null;
  assigned_by_username: string | null;
  assigned_at: string;
}

export type AuditDomain =
  | "rbac"
  | "countries"
  | "franchise"
  | "food"
  | "restaurant"
  | "services"
  | "users"
  | "ride"
  | "classified"
  | "finance"
  | "system";

export interface AuditLogEntry {
  id: number;
  user: number | null;
  username: string | null;
  action: string;
  domain: AuditDomain;
  resource_type: string;
  resource_id: string;
  changes: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface AssignRolePayload {
  user_id: number;
  role_id: number;
  scope_type?: string;
  scope_reference_id?: number | null;
}

export interface CreateRolePayload {
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
  permission_ids: number[];
}

export interface UpdateRolePayload {
  name?: string;
  display_name?: string;
  description?: string;
  is_active?: boolean;
  permission_ids?: number[];
}

export interface ImpersonateResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    roles: string[];
  };
}

// ─── API Functions ───────────────────────────────────────────────────────────

// Roles
export async function fetchRoles(): Promise<AdminRole[]> {
  return api.get<AdminRole[]>("/rbac/roles/");
}

export async function fetchRoleDetail(id: number): Promise<AdminRoleDetail> {
  return api.get<AdminRoleDetail>(`/rbac/roles/${id}/`);
}

export async function createRole(
  data: CreateRolePayload
): Promise<AdminRoleDetail> {
  return api.post<AdminRoleDetail>("/rbac/roles/", data);
}

export async function updateRole(
  id: number,
  data: UpdateRolePayload
): Promise<AdminRoleDetail> {
  // PATCH (partial update) -- callers like the EditPermissionsDialog
  // send only ``permission_ids``. PUT would require ``name`` and
  // ``display_name`` too and the backend serializer rejects the
  // partial body with a 400.
  return api.patch<AdminRoleDetail>(`/rbac/roles/${id}/`, data);
}

// Permissions
export async function fetchPermissions(): Promise<Permission[]> {
  return api.get<Permission[]>("/rbac/permissions/");
}

/**
 * Flip whether a permission may be delegated to a franchise entity.
 *
 * This is the delegation policy that used to be a hardcoded frozenset in the
 * backend (`franchise/grants.py: NON_DELEGATABLE`). Requires `rbac.manage` --
 * the narrower `permission.view` that makes the catalogue readable is not
 * enough, because this is a write to a security control. Every change is
 * audited under `domain="rbac"`.
 */
export async function updatePermissionDelegation(
  id: number,
  isDelegatable: boolean
): Promise<Permission> {
  return api.patch<Permission>(`/rbac/permissions/${id}/delegation/`, {
    is_delegatable: isDelegatable,
  });
}

// User role assignments
export async function fetchUserRoles(params?: {
  role_id?: number;
  is_active?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<UserRoleAssignment>> {
  const qp = new URLSearchParams();
  if (params?.role_id) qp.set("role_id", String(params.role_id));
  if (params?.is_active !== undefined) qp.set("is_active", String(params.is_active));
  if (params?.search) qp.set("search", params.search);
  if (params?.page) qp.set("page", String(params.page));
  if (params?.page_size) qp.set("page_size", String(params.page_size));
  const query = qp.toString();
  return api.get<PaginatedResponse<UserRoleAssignment>>(
    `/rbac/user-roles/${query ? `?${query}` : ""}`
  );
}

export async function assignUserRole(
  data: AssignRolePayload
): Promise<UserRoleAssignment> {
  return api.post<UserRoleAssignment>("/rbac/user-roles/assign/", data);
}

export async function revokeUserRole(id: number): Promise<void> {
  return api.delete(`/rbac/user-roles/${id}/revoke/`);
}

// Audit logs
export interface AuditLogParams {
  domain?: string;
  resource_type?: string;
  action?: string;
  user_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export async function fetchAuditLogs(
  params?: AuditLogParams
): Promise<PaginatedResponse<AuditLogEntry>> {
  const qp = new URLSearchParams();
  if (params?.domain) qp.set("domain", params.domain);
  if (params?.resource_type) qp.set("resource_type", params.resource_type);
  if (params?.action) qp.set("action", params.action);
  if (params?.user_id) qp.set("user_id", String(params.user_id));
  if (params?.date_from) qp.set("date_from", params.date_from);
  if (params?.date_to) qp.set("date_to", params.date_to);
  if (params?.search) qp.set("search", params.search);
  if (params?.page) qp.set("page", String(params.page));
  if (params?.page_size) qp.set("page_size", String(params.page_size));
  const query = qp.toString();
  return api.get<PaginatedResponse<AuditLogEntry>>(
    `/rbac/audit-logs/${query ? `?${query}` : ""}`
  );
}

// Impersonation
export async function impersonateUser(
  userId: number
): Promise<ImpersonateResponse> {
  return api.post<ImpersonateResponse>(`/rbac/impersonate/${userId}/`);
}

export async function endImpersonation(): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/rbac/impersonate/end/", {
    original_username: localStorage.getItem("impersonation_original_user")
      ? JSON.parse(localStorage.getItem("impersonation_original_user")!).username
      : "",
  });
}
